import React, { useState, useEffect } from "react";
import axios from "axios";
import { showErrorToast, showSuccessToast } from "../utils/toaster";
import Loader from "../Components/Loader";
import BillingForm from "../Components/checkoutcomponents/BillingForm";
import Address from "../Components/checkoutcomponents/address";
import AddProductInvoice from "../Pages/UserProductInvoice";
import useDebounce from "../hooks/useDebounce";
import logo from "../Components/Assets/buillogo6.png";
import { baseurl } from "../config/url";
import { statesInIndia } from "../utils/data";

// ---- helper: always work with valid items only ----
const getValidItems = (items) =>
  (items || []).filter((item) => item && item.productId);

// ---- transformCartData made safe ----
function transformCartData(cartData, shippingCost) {
  let userId = localStorage.getItem("userId");
  const validItems = getValidItems(cartData?.items);

  const taxes = validItems.reduce((totalTax, item) => {
    const price = Number(item.productId?.price) || 0;
    const qty = Number(item.quantity) || 1;
    const gstRate = Number(item.productId?.gstNumber) || 0;
    const productPrice = price * qty;
    return totalTax + productPrice * (gstRate / 100);
  }, 0);

  const transformedData = {
    items: validItems.map((item) => ({
      product: item.productId._id,
      seller: item.productId.seller,
      price: item.productId.price,
      quantity: item.quantity,
      igst: item.productId.gstNumber,
      hsnCode: item.productId.hsnCode,
    })),
    userId,
    deliveryCharges: Math.round(Number(shippingCost) || 0),
    totalgst: taxes,
  };
  return transformedData;
}

const Checkout = () => {
  const [formData, setFormData] = useState({
    mobileNumber: "",
    postalCode: "",
    houseNumberOrApartment: "",
    areaOrStreet: "",
    landmark: "",
    cityOrTown: "",
    selectedState: "Maharashtra",
    latitude: "",
    longitude: "",
    selectedPlace: "",
  });

  const [breakdownCharges, setBreakdownCharges] = useState({
    baseCharge: 0,
    perKmCharge: 0,
    weightCharge: 0,
  });

  const [cart, setcart] = useState(null); // will hold cart object, not array
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [isNewAddress, setIsNewAddress] = useState(true);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [totalItemsCost, setTotalItemsCost] = useState(0);
  const [tax, setTax] = useState(0);
  const [invoiceData, setInvoiceData] = useState({
    visibilityPrice: 0,
    powerPackPrice: 0,
    ammount: 0,
    maxquantity: 0,
    saleMode: "",
    products: [],
    visibilityLevel: "",
    hsnCode: "",
  });

  const debounce = useDebounce(handleSearchPlace, 700);

  // Toggle Show All
  const toggleShowAllAddresses = () => {
    setShowAllAddresses(!showAllAddresses);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateAddressForm = (formData) => {
    if (!formData.mobileNumber || !/^\d{10}$/.test(formData.mobileNumber)) {
      return "Please enter a valid 10-digit mobile number.";
    }
    if (!formData.postalCode || !/^\d{6}$/.test(formData.postalCode)) {
      return "Please enter a valid 6-digit postal code.";
    }
    if (!formData.houseNumberOrApartment.trim()) {
      return "Please enter your house number or apartment.";
    }
    if (!formData.areaOrStreet.trim()) {
      return "Please enter your area or street.";
    }
    if (!formData.cityOrTown.trim()) {
      return "Please enter your city or town.";
    }
    if (!formData.selectedState) {
      return "Please select a state.";
    }
    if (!formData.selectedPlace) {
      return "Please select your place.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMessage = validateAddressForm(formData);
    if (errorMessage) {
      showErrorToast(errorMessage);
      return;
    }
    showSuccessToast("form fill");

    const userId = localStorage.getItem("userId");
    const addressData = {
      userId: userId,
      mobileNumber: formData.mobileNumber,
      postalCode: formData.postalCode,
      houseNumberOrApartment: formData.houseNumberOrApartment,
      areaOrStreet: formData.areaOrStreet,
      landmark: formData.landmark,
      cityOrTown: formData.cityOrTown,
      latitude: formData.latitude,
      longitude: formData.longitude,
      selectedPlace: formData.selectedPlace,
      selectedState: formData.selectedState,
    };

    setLoading(true);

    try {
      const response = await axios.post(
        `${baseurl}/api/address/add`,
        addressData
      );
      console.log(response.data);
      getAllAddress();
      showSuccessToast("The address has been saved successfully.");

      setFormData({
        mobileNumber: "",
        postalCode: "",
        houseNumberOrApartment: "",
        areaOrStreet: "",
        landmark: "",
        cityOrTown: "",
        selectedState: "",
        latitude: "",
        longitude: "",
        selectedPlace: "",
      });
    } catch (error) {
      const backendMessage = error.response?.data?.message;
      const fallbackMessage = "Something went wrong while saving your address.";
      showErrorToast(backendMessage || fallbackMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all addresses
  const getAllAddress = async () => {
    let userId = localStorage.getItem("userId");
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}/api/address/get/${userId}`);
      let address = response?.data?.address || [];
      setAddresses(address);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      showErrorToast("Error fetching addresses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---- FIXED: handleAddressSelection with safe productId check ----
  function handleAddressSelection(address) {
    console.log("Selected Address:", address);

    if (!cart || !Array.isArray(cart.items)) {
      // no cart yet, just select address
      setSelectedAddress(address._id);
      setIsNewAddress(false);
      setSearchQuery(address.selectedPlace || "");
      setFormData({
        mobileNumber: address.mobileNumber,
        postalCode: address.postalCode,
        houseNumberOrApartment: address.houseNumberOrApartment,
        areaOrStreet: address.areaOrStreet,
        landmark: address.landmark,
        cityOrTown: address.cityOrTown,
        selectedState: address.selectedState,
        latitude: address.latitude,
        longitude: address.longitude,
        selectedPlace: address.selectedPlace,
      });
      return;
    }

    const selectedState = (address.selectedState || "").trim().toLowerCase();
    if (!selectedState) {
      showErrorToast("Selected address does not have a valid state.");
      return;
    }

    const validItems = getValidItems(cart.items);

    const unavailableProducts = validItems.filter((item) => {
      const productStates = item.productId.states;

      // If states not defined or not an array, treat as available everywhere
      if (!Array.isArray(productStates)) return false;

      const normalizedStates = productStates
        .map((state) => (state || "").trim().toLowerCase())
        .filter(Boolean);

      return !normalizedStates.includes(selectedState);
    });

    if (unavailableProducts.length > 0) {
      console.error("Unavailable Products:", unavailableProducts);

      showErrorToast(
        `The following products cannot be delivered to the selected address in ${
          address.selectedState
        }: ${unavailableProducts
          .map((item) => item.productId.name)
          .join(
            ", "
          )}. Please choose a different address or update your cart.`
      );

      return;
    }

    console.log("All products are deliverable to the selected address.");

    setSelectedAddress(address._id);
    setIsNewAddress(false);
    setSearchQuery(address.selectedPlace);
    setFormData({
      mobileNumber: address.mobileNumber,
      postalCode: address.postalCode,
      houseNumberOrApartment: address.houseNumberOrApartment,
      areaOrStreet: address.areaOrStreet,
      landmark: address.landmark,
      cityOrTown: address.cityOrTown,
      selectedState: address.selectedState,
      latitude: address.latitude,
      longitude: address.longitude,
      selectedPlace: address.selectedPlace,
    });

    fetchDeliveryCharge(address.latitude, address.longitude);
  }

  const handleAddNewAddress = () => {
    setIsNewAddress(true);
    setFormData({
      mobileNumber: "",
      postalCode: "",
      houseNumberOrApartment: "",
      areaOrStreet: "",
      landmark: "",
      cityOrTown: "",
      selectedState: "Maharashtra",
      latitude: "",
      longitude: "",
      selectedPlace: "",
    });
    setSearchQuery("");
    setSelectedAddress("");
  };

  // Search place
  async function handleSearchPlace(place) {
    if (searchQuery) {
      try {
        const response = await axios.get(
          `https://nominatim.openstreetmap.org/search?format=json&q=${place}`
        );
        setSearchResults(response.data);
      } catch (error) {
        console.error("Error searching for place:", error);
        showErrorToast("Error searching for place. Please try again.");
      }
    } else {
      setSearchResults([]);
    }
  }

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const createOrderAndStorePayment = async (data) => {
    let orderData = transformCartData(cart, shippingCost);
    orderData.address = selectedAddress;
    let userId = localStorage.getItem("userId");
    let token = localStorage.getItem("token");

    let paymentData = {
      orderCreationId: data?.orderCreationId,
      razorpayOrderId: data?.razorpayOrderId,
      razorpayPaymentId: data?.razorpayPaymentId,
      razorpaySignature: data.razorpaySignature,
      userId: userId,
      cartId: cart?._id,
    };
    console.log("paymentData", paymentData);
    try {
      const [orderResponse, paymentResponse] = await Promise.all([
        axios.post(`${baseurl}/api/order/create-order`, orderData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.post(`${baseurl}/api/payment/payment-store-todb`, paymentData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (orderResponse.status === 201) {
        const { totalAmount, gst } = orderResponse.data;
        setTotalItemsCost(totalAmount - gst);
        setTax(gst);
        showSuccessToast("Order created successfully");
      }

      if (paymentResponse.status === 200) {
        console.log("Payment Response:", paymentResponse.data);
      }
    } catch (error) {
      console.error("Error in createOrderAndStorePayment:", error);
      showErrorToast("Error creating order or processing payment");
    }
  };

  // ------------ Other payment (made safe) -------------
  const handleOtherPaymentSubmit = async (otherPaymentDetails) => {
    if (shippingCost === 0) {
      showErrorToast(
        "Cannot proceed with payment. All products exceed the maximum delivery distance."
      );
      return;
    }
    if (
      !otherPaymentDetails.method ||
      !otherPaymentDetails.bankName ||
      !otherPaymentDetails.transactionId ||
      !otherPaymentDetails.ifscCode ||
      !otherPaymentDetails.accountNumber
    ) {
      showErrorToast("Please fill all required fields.");
      return;
    }

    const validItems = getValidItems(cart?.items);
    const productIds = validItems.map((item) => item.productId._id);
    const userId = localStorage.getItem("userId");
    const addressId = selectedAddress;

    if (!addressId) {
      showErrorToast("Please select a delivery address.");
      return;
    }

    const grandTotal = totalItemsCost + tax + shippingCost;
    const payload = {
      ...otherPaymentDetails,
      amount: grandTotal,
      productIds,
      userId,
      address: addressId,
      date: new Date().toISOString(),
    };

    try {
      const paymentResponse = await axios.post(
        `${baseurl}/api/otherpayment/other-payment-user`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const paymentData = paymentResponse.data;

      const orderPayload = {
        user: userId,
        address: addressId,
        items: validItems.map((item) => ({
          product: item.productId._id,
          quantity: item.quantity,
          price: item.productId.price,
          seller: item.productId.seller,
          igst: item.productId.gstNumber,
          subtotalwithoutgst: item.productId.price * item.quantity,
          subtotalwithgst:
            item.productId.price *
            item.quantity *
            (1 + item.productId.gstNumber / 100),
        })),
        otherPaymentId: paymentData.payment._id,
        totalAmount: grandTotal,
        totalGst: tax,
        deliveryCharges: shippingCost,
      };

      const orderResponse = await axios.post(
        `${baseurl}/api/order/create-order`,
        orderPayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (orderResponse.status === 201) {
        showSuccessToast("Order created successfully!");
        console.log("Order Created Successfully:", orderResponse.data);
      }
    } catch (error) {
      console.error("Error in handleOtherPaymentSubmit:", error);
      showErrorToast("Failed to process payment or create order.");
    }
  };

  // Place selection
  const handlePlaceSelection = (place) => {
    let latitude = place.lat;
    let longitude = place.lon;

    setFormData((prevData) => ({
      ...prevData,
      latitude: latitude,
      longitude: longitude,
      selectedPlace: place.display_name,
    }));

    setSearchResults([]);
    setSearchQuery(place.display_name);
  };

  // ---- Fetch cart, made safe ----
  const fetchCart = async () => {
    const userId = localStorage.getItem("userId");
    try {
      const response = await axios.get(
        `${baseurl}/api/cart/get-cart/${userId}`
      );
      const cartData = response?.data;
      setcart(cartData);

      const validItems = getValidItems(cartData?.items);

      const totalCost = validItems.reduce((acc, item) => {
        const price = Number(item.productId?.price) || 0;
        const qty = Number(item.quantity) || 1;
        return acc + price * qty;
      }, 0);
      setTotalItemsCost(totalCost);

      setInvoiceData((prev) => ({
        ...prev,
        products: validItems.map((item) => ({
          productId: item.productId._id,
          productName: item.productId.name,
          productPrice: item.productId.price,
          quantity: item.quantity,
          hsnCode: item.productId.hsnCode,
          gstNumber: item.productId.gstNumber,
          seller: item.productId.seller,
          image: item.productId.image,
        })),
      }));
    } catch (error) {
      console.error(error);
      showErrorToast("Error while fetching products data.");
    }
  };

  // ---- Fetch delivery charge, made safe ----
  const fetchDeliveryCharge = async (lat, lon) => {
    const userAddress = { latitude: lat, longitude: lon };

    const sellerOrders = {};
    getValidItems(cart?.items).forEach((item) => {
      const sellerId = item.productId.seller;
      const productId = item.productId._id;
      const productWeight = item.productId.weight;

      if (!sellerOrders[sellerId]) {
        sellerOrders[sellerId] = [];
      }
      sellerOrders[sellerId].push({ productId, weight: productWeight });
    });

    const transformedData = {
      sellerOrders: Object.entries(sellerOrders).map(
        ([sellerId, products]) => ({
          sellerId,
          products,
        })
      ),
      userAddress,
    };

    try {
      const response = await axios.post(
        `${baseurl}/api/deliverycharge/charges`,
        transformedData
      );

      const { totalCharge, chargesBreakdown } = response.data;

      if (chargesBreakdown.every((seller) => seller.exceedsMaxDistance)) {
        showErrorToast(
          "All products in your cart exceed the maximum delivery distance. Please update your cart or address."
        );
        setShippingCost(0);
        return;
      }

      const exceededSellers = chargesBreakdown.filter(
        (seller) => seller.exceedsMaxDistance
      );

      if (exceededSellers.length > 0) {
        showErrorToast(
          `Some products exceed the delivery distance: ${exceededSellers
            .map((s) => s.sellerName)
            .join(", ")}.`
        );
      }

      setShippingCost(totalCharge);
    } catch (error) {
      console.error("Error while fetching delivery charges:", error);
      showErrorToast("Error while fetching delivery charges.");
    }
  };

  useEffect(() => {
    getAllAddress();
    fetchCart();
  }, []);

  // Razorpay
  const displayRazorpay = async (amount) => {
    if (!selectedAddress) {
      showErrorToast("Please select an address to proceed.");
      return;
    }
    if (shippingCost === 0) {
      showErrorToast(
        "Cannot proceed with payment. All products exceed the maximum delivery distance."
      );
      return;
    }
    let usercredentials = JSON.parse(localStorage.getItem("usercredentials"));
    try {
      const response = await axios.post(
        `${baseurl}/api/payment/create-payment-order`,
        { amount }
      );

      if (response.status !== 200) {
        showErrorToast(response.data.message || "Failed to create order");
        return;
      }

      const { order, key } = response.data;
      const { id: order_id, currency } = order;

      const options = {
        key,
        amount: amount,
        currency,
        name: "BuildPro",
        description: "BuildPro Payment",
        image: logo,
        order_id,
        handler: async function (response) {
          const {
            razorpay_payment_id,
            razorpay_order_id,
            razorpay_signature,
          } = response;
          const data = {
            orderCreationId: order_id,
            razorpayPaymentId: razorpay_payment_id,
            razorpayOrderId: razorpay_order_id,
            razorpaySignature: razorpay_signature,
          };
          if (data.razorpayPaymentId) createOrderAndStorePayment(data);
          console.log("Payment Details:", data);
          showSuccessToast("Payment Successful!");
        },
        prefill: {
          name: usercredentials.name,
          email: usercredentials.email,
          contact: formData.mobileNumber,
        },
        notes: {
          address: "Customer Address",
        },
        theme: {
          color: "#F37254",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Error in Razorpay payment:", error);
      showErrorToast("Payment failed. Please try again later.");
    }
  };

  return (
    <div className="p-5">
      <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">
        Checkout
      </h1>

      <Address
        addresses={addresses}
        loading={loading}
        selectedAddress={selectedAddress}
        showAllAddresses={showAllAddresses}
        handleAddNewAddress={handleAddNewAddress}
        handleAddressSelection={handleAddressSelection}
        toggleShowAllAddresses={toggleShowAllAddresses}
      />

      <div className="flex md:flex-row flex-col gap-8 max-w-7xl mx-auto ">
        <form
          onSubmit={handleSubmit}
          className="w-full lg:w-[100%] mx-auto bg-white rounded-lg shadow-md p-6 border"
        >
          {/* form fields... (unchanged) */}
          {/* Keeping exactly the same fields as in your code */}
          <div className="mb-4">
            <label
              htmlFor="mobileNumber"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Mobile Number:
            </label>
            <input
              type="text"
              id="mobileNumber"
              name="mobileNumber"
              disabled={selectedAddress}
              value={formData.mobileNumber}
              onChange={handleChange}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Enter your mobile number"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="postalCode"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Postal Code:
            </label>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              disabled={selectedAddress}
              value={formData.postalCode}
              onChange={handleChange}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Enter your postal code"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="houseNumberOrApartment"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              House Number/Apartment:
            </label>
            <input
              type="text"
              id="houseNumberOrApartment"
              name="houseNumberOrApartment"
              disabled={selectedAddress}
              value={formData.houseNumberOrApartment}
              onChange={handleChange}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Enter your house number or apartment"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="areaOrStreet"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Area or Street:
            </label>
            <input
              type="text"
              id="areaOrStreet"
              name="areaOrStreet"
              disabled={selectedAddress}
              value={formData.areaOrStreet}
              onChange={handleChange}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Enter your area or street"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="landmark"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Landmark :
            </label>
            <input
              type="text"
              id="landmark"
              name="landmark"
              disabled={selectedAddress}
              value={formData.landmark}
              onChange={handleChange}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Enter landmark"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="cityOrTown"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              City or Town:
            </label>
            <input
              type="text"
              id="cityOrTown"
              name="cityOrTown"
              disabled={selectedAddress}
              value={formData.cityOrTown}
              onChange={handleChange}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Enter your city or town"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="selectedState"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              State:
            </label>
            <select
              id="selectedState"
              name="selectedState"
              disabled={selectedAddress}
              value={formData.selectedState}
              onChange={handleChange}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              {statesInIndia.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4 relative">
            <label
              htmlFor="searchPlace"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Search Place:
            </label>
            <input
              type="text"
              id="searchPlace"
              disabled={selectedAddress}
              value={searchQuery}
              onChange={(e) => {
                debounce(e.target.value);
                handleSearchChange(e);
              }}
              className="w-full px-3 py-1 border border-gray-400 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Search for a place"
            />
            <div className="absolute left-0 right-0 mt-2 z-10 bg-white shadow-lg rounded-lg max-h-60 overflow-y-auto border border-gray-200">
              {searchResults.length > 0 && (
                <ul className="space-y-2">
                  {searchResults.map((place, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-800 px-4 py-2 rounded-lg cursor-pointer hover:bg-indigo-100 transition duration-200 ease-in-out"
                      onClick={() => handlePlaceSelection(place)}
                    >
                      {place.display_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {isNewAddress && (
            <div className="mt-6">
              <button
                type="submit"
                className="bg-blue-500 text-white font-bold py-3 px-6 rounded-md w-full focus:outline-none hover:bg-blue-600 flex justify-center items-center"
              >
                {loading ? <Loader /> : "Save Address"}
              </button>
            </div>
          )}
        </form>

        <div className="w-full lg:w-[90%] mx-auto bg-white p-8 shadow-md rounded-lg border">
          <AddProductInvoice
            {...invoiceData}
            ammount={totalItemsCost}
            shippingCost={shippingCost}
            saleMode="Sale By OGCS"
          />

          <BillingForm
            shippingCost={shippingCost}
            displayRazorpay={displayRazorpay}
            totalItemsCost={totalItemsCost}
            submitOtherPayment={handleOtherPaymentSubmit}
            tax={tax}
            cart={cart}
          />

          <div className="charge-breakdown mt-4 text-sm text-gray-700">
            <p>Base Charge: ₹{breakdownCharges.baseCharge}</p>
            <p>Per Km Charge: ₹{breakdownCharges.perKmCharge}</p>
            <p>Weight Charge: ₹{breakdownCharges.weightCharge}</p>
            <p>Total Shipping Cost: ₹{shippingCost}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
